import Flag from '@components/Flag/Flag';
import InlineCombobox, {
  InlineComboboxList,
  InlineComboboxListItem,
} from '@components/InlineCombobox/InlineCombobox';
import SearchBar from '@components/SearchBar/SearchBar';
import { isSupportedCountry } from 'libphonenumber-js';
import { useCallback, useEffect, useMemo, useState } from 'react';

import countries from './countries.json';

const supportedCountries = filterCountries(
  countries as { code: string; name: string; countryCallingCode: string }[], // Typescript being wierd
);

import useNode from '@hooks/use-node';
import type { CountryCode } from 'libphonenumber-js';

import styles from './FlagSelector.module.scss';

interface CountryWithCallingCode extends Country {
  countryCallingCode: string;
}

interface FlagSelectorProps {
  show: boolean;
  selectedCountryCode: CountryCode | undefined;
  onSelect: (countryCode: CountryCode) => void;
  containerNode?: HTMLElement;
}

const COUNTRY_ALIASES: Partial<Record<CountryCode, string[]>> = {
  US: ['America', 'USA'],
  AE: ['UAE'],
};

const FlagSelector: React.FC<React.PropsWithChildren<FlagSelectorProps>> = ({
  show,
  selectedCountryCode,
  onSelect,
  containerNode,
}) => {
  const [countrySearchValue, setCountrySearchValue] = useState('');
  const [searchBarNode, searchBarRef] = useNode<HTMLInputElement>();

  const filteredCountries = useMemo(() => {
    if (countrySearchValue) {
      return supportedCountries.filter(
        ({ code, name, countryCallingCode }) =>
          code.toLowerCase().includes(countrySearchValue.toLowerCase()) ||
          name.toLowerCase().includes(countrySearchValue.toLowerCase()) ||
          COUNTRY_ALIASES[code]?.some((alias) =>
            alias.toLowerCase().includes(countrySearchValue.toLowerCase()),
          ) ||
          countryCallingCode.includes(countrySearchValue.replace('+', '')),
      );
    } else return supportedCountries;
  }, [countrySearchValue]);

  const handleSelect = useCallback(
    (countryCode: string) => {
      onSelect(countryCode as CountryCode);
    },
    [onSelect],
  );

  useEffect(() => {
    if (show && searchBarNode) {
      searchBarNode.focus();
    }
  }, [show, searchBarNode]);

  useEffect(() => {
    if (!show) {
      setCountrySearchValue('');
    }
  }, [show]);

  return (
    <div className={styles['flag-selector']}>
      <InlineCombobox show={show} onSelect={handleSelect} containerNode={containerNode}>
        <div className={styles['search-bar-container']}>
          <SearchBar
            placeholder="Search for your country"
            value={countrySearchValue}
            realtime={false}
            onChange={(searchValue) => setCountrySearchValue(searchValue)}
            ref={searchBarRef}
          />
        </div>

        {filteredCountries.length > 0 && (
          <InlineComboboxList>
            <ul>
              {filteredCountries.map(({ code, name, countryCallingCode }) => (
                <InlineComboboxListItem value={code} selected={selectedCountryCode === code} key={code}>
                  <div className={styles['flag-list-item']}>
                    <span className={styles['flag-container']}>
                      <Flag countryCode={code} />
                    </span>
                    <div className={styles['flag-details-container']}>
                      <span className={styles['country']}>{name}</span>
                      <span className={styles['country-code']}>(+{countryCallingCode})</span>
                    </div>
                  </div>
                </InlineComboboxListItem>
              ))}
            </ul>
          </InlineComboboxList>
        )}

        {filteredCountries.length === 0 && (
          <InlineComboboxList>
            <div className={styles['no-match-message']}>
              <p>
                There were no countries that corresponded to <b>&quot;{countrySearchValue}&quot;</b>
              </p>
            </div>
          </InlineComboboxList>
        )}
      </InlineCombobox>
    </div>
  );
};

interface Country {
  code: CountryCode;
  name: string;
}

/**
 * Only returns the countries that are supported by libphonenumber-js. This is necessary as there is a discrepency
 * between the countries for which we have a flag in the spritesheet and those that libphonenumber-js supports.
 *
 * We don't actually have any unsupported countries in countries.json (e.g. Antarctica, Pitcairn, Netherlands Antilles) so
 * this is mainly just a safeguard for the future
 *
 * @see https://www.freakflagsprite.com/
 * @param countries The list of countries with an associated flag
 */
function filterCountries(
  countries: { code: string; name: string; countryCallingCode: string }[],
): CountryWithCallingCode[] {
  return countries.filter((country) => isSupportedCountry(country.code)) as CountryWithCallingCode[];
}

export default FlagSelector;
export { countries };
